import {Button, Chip, Stack, Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import AttachIcon from '~/icons/svg/AttachIcon.svg?react'
import type {ComplianceRequirementGridRow} from "~/pages/supplier-360/types/Supplier360Types";
import {formatDate} from "~/components/data-grid/cells/gridCellFormat";
import {
    centered,
    daysLeftLine,
    emptyCell,
    subLine,
} from "~/components/data-grid/cells/gridCells";
import {STATUS_CHIP} from "~/components/status-chip/statusChips";
import {VERIFICATION_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceVerificationChips";

type ComplianceBuRequirementColumnOptions = {
    /** The BU's name, for the "Held at …" applicability line. */
    businessUnitName: string;
    onOpen: (row: ComplianceRequirementGridRow) => void;
};

/**
 * The expanded Business units row (`SxbRequirements`): one BU's own + inherited requirements, in
 * the design's sub-grid columns. A small, unpaginated list — sorting is off, as in the design.
 */
export function getComplianceBuRequirementColumns({businessUnitName, onOpen}: ComplianceBuRequirementColumnOptions): GridColDef<ComplianceRequirementGridRow>[] {
    return [
        {
            field: 'name',
            headerName: 'Compliance requirement',
            flex: 1.6,
            minWidth: 240,
            sortable: false,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2" fontWeight={600} noWrap>{row.name}</Typography>
                    {subLine(row.kind)}
                </>,
            ),
        },
        {
            field: 'layer',
            headerName: 'Applicability',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            renderCell: ({row}) => centered(
                <>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={row.layer === 'company_wide' ? 'default' : 'secondary'}
                        label={row.layer === 'company_wide' ? 'Company-wide' : 'Business-unit specific'}
                        sx={{alignSelf: 'flex-start'}}
                    />
                    {subLine(row.layer === 'company_wide' ? 'Inherited by every business unit' : `Held at ${businessUnitName}`)}
                </>,
            ),
        },
        {
            /** `latest_evidence_name` (§2.8) — the record's most recent upload. */
            field: 'latest_evidence_name',
            headerName: 'Evidence',
            flex: 1.4,
            minWidth: 220,
            sortable: false,
            renderCell: ({row}) => centered(
                row.latest_evidence_name
                    ? (
                        <Typography variant="body2" noWrap sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <AttachIcon width={14} height={14}/> {row.latest_evidence_name}
                        </Typography>
                    )
                    : <Typography variant="body2" color="error.main" fontStyle="italic">No evidence on file</Typography>,
            ),
        },
        {
            field: 'expires_at',
            headerName: 'Valid until',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2">{formatDate(row.expires_at)}</Typography>
                    {daysLeftLine(row.expires_at)}
                </>,
            ),
        },
        {
            field: 'verification_status',
            headerName: 'Verification',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: ({row}) => row.verification_status ? centered(VERIFICATION_CHIP[row.verification_status]) : emptyCell(),
        },
        {
            field: 'state',
            headerName: 'State',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: ({row}) => centered(STATUS_CHIP[row.state]),
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: ({row}) => (
                <Stack direction="row" sx={{alignItems: 'center', height: '100%'}}>
                    <Button
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpen(row);
                        }}
                        sx={{textTransform: 'none', minWidth: 0}}
                    >
                        Details
                    </Button>
                </Stack>
            ),
        },
    ];
}
